import { Chip, Container, Fab, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import TabSelector from '../IntroductionModal/TabSelector';
import { isEmpty, isEqual } from '../../utils/lodash';
import AddIcon from '@mui/icons-material/Add';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import CustomButton, { ICustomButton } from '../Button/CustomButton';

export interface ITabSelector {
    onChange?: (selection: string[]) => void;
    maxSelection?: number;
    minSelection?: number;
    label?: string;
    overallStyle?: React.CSSProperties;
    options: string[];
    value: string[];
    normalButton?: boolean;
    buttonProps?: ICustomButton;
    containerStyle?: React.CSSProperties;
    overrideSubmit?: boolean;
}

const MAX_CHIP = 1;

const PillSelector: React.FC<ITabSelector> = ({
    label,
    maxSelection,
    minSelection,
    onChange,
    options,
    value,
    normalButton,
    buttonProps,
    containerStyle,
    overrideSubmit,
}) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const { device, themeStyle } = useThemeContext();
    const onHandleModal = () => {
        setOpen(!open);
    };
    const handleOnChange = (selected: string[]) => {
        setSelected(selected);
    };

    useEffect(() => {
        if (value) {
            setSelected(value);
        }
    }, [value]);

    const handleDelete = (selection: string) => {
        const filtered = selected.filter(filter => filter !== selection);
        setSelected(filtered);
        onChange && onChange(filtered);
    };

    const handleOnSubmit = () => {
        if (!isEqual(value, selected) || (overrideSubmit && !isEmpty(selected))) {
            onChange && onChange(selected);
        }
        onHandleModal();
    };
    return (
        <Container disableGutters style={{ gap: '20px', ...containerStyle }}>
            <Modal
                open={open}
                onClose={onHandleModal}
                style={{
                    backdropFilter: 'blur(12px)', // Adjust the blur level => Adjusted from 12px to 8px
                    backgroundColor: device.isMobile
                        ? theme[themeStyle].modal_dark_mobile
                        : theme[themeStyle].modal_dark, // Adjust the darkness (0.6 for 60% opacity)
                    display: 'flex',
                    zIndex: 10100,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                slotProps={{
                    backdrop: {
                        timeout: 600,
                    },
                }}>
                <>
                    <TabSelector
                        themeStyle={themeStyle}
                        onSelect={option => {}}
                        onChange={handleOnChange}
                        maxSelection={maxSelection}
                        initialValue={selected}
                        maxSelectedDisableState={selected.length === maxSelection}
                        skipRefresh
                        shouldShowButton
                        disableButton={false}
                        onClickButton={handleOnSubmit}
                        buttonText={
                            ((isEmpty(selected) || isEqual(value, selected)) && !overrideSubmit) ||
                            (overrideSubmit && isEmpty(selected))
                                ? 'close'
                                : 'Submit'
                        }
                        step={{
                            options: options,
                            displayTitle: label,
                            displayType: 'tabs',
                        }}
                    />
                </>
            </Modal>
            {!normalButton && (
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MEDIUM_HEADER,
                        color: theme[themeStyle].tag_white,
                        fontSize: 15,
                        marginBottom: '20px',
                    }}>
                    {label}
                </Typography>
            )}
            {normalButton ? (
                <CustomButton
                    onClick={onHandleModal}
                    customStyle={{
                        width: '100px',
                    }}
                    label={true ? 'Submit' : 'Submitted'}
                    textStyle={{
                        fontSize: 12,
                    }}
                    {...buttonProps}
                />
            ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                    {!isEmpty(selected) &&
                        selected.slice(0, MAX_CHIP).map((selection, index) => (
                            <Chip
                                label={selection}
                                key={index}
                                onDelete={() => handleDelete(selection)}
                                sx={{
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].tertiary_white,
                                    borderColor: theme[themeStyle].true_white,
                                    '& .MuiChip-deleteIcon': {
                                        color: theme[themeStyle].tertiary_white,
                                        '&:hover': {
                                            color: theme[themeStyle].tertiary_button,
                                        },
                                    },
                                }}
                            />
                        ))}
                    {selected.length > MAX_CHIP && (
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MEDIUM_HEADER,
                                color: theme[themeStyle].tag_white,
                                fontSize: 14,
                            }}>
                            +{selected.length - MAX_CHIP}
                        </Typography>
                    )}
                    {!!maxSelection && selected.length < maxSelection && (
                        <Fab
                            size="small"
                            sx={{
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: theme[themeStyle].gray_message },
                            }}
                            onClick={onHandleModal}>
                            <AddIcon />
                        </Fab>
                    )}
                </Stack>
            )}
        </Container>
    );
};

export { PillSelector };
